import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { ensurePlusSdkLoaded } from '../../lib/plus/plusClient';
import { DABBEH_PLUS_PRODUCT, DABBEH_PRICE_TOMAN, DABBEH_SUBSCRIPTION_DAYS } from './constants';
import type { DabbehSubscription } from './types';

/**
 * Real payment for the queue subscription through Paziresh24 Plus (plus.paziresh24.com), product
 * DABBEH_PLUS_PRODUCT: our own checkout page, Plus's headless SDK underneath — the patient pays from the
 * wallet, or tops it up through the Katibe gateway and comes back to the checkout page, where the SDK
 * finishes the payment by itself. Plus keeps the subscription, so it is the same on every device, and the
 * queue service (dabbeh) asks Plus (server to server) before seating anyone.
 * Empty DABBEH_PLUS_PRODUCT = the prototype's instant purchase.
 *
 * The SDK script is the one the site already loads for Plus (src/lib/plus/plusClient.ts); the queue only
 * adds its own instance, on its own screens (sheet, checkout).
 */
export const PLUS_BASE_URL = 'https://plus.paziresh24.com';

export const isRealBilling = () => !!DABBEH_PLUS_PRODUCT;

const DAY = 24 * 3600_000;

export interface PlusPlan {
  id: string;
  months: number;
  /** toman */
  amount: number;
}

export interface PlusStatus {
  active: boolean;
  customerId?: string;
  entitlements?: Array<{ id?: string }>;
  /** unix seconds */
  current_period_end?: number | null;
}

export interface PlusInstance {
  getPlans: () => Promise<PlusPlan[]>;
  getStatus: () => Promise<PlusStatus>;
  startCheckout: (
    priceId: string,
    options?: { autoRenew?: boolean },
  ) => Promise<{ ok?: boolean; status?: PlusStatus; redirecting?: boolean; dismissed?: boolean } | undefined>;
  closePaywall: (immediate?: boolean) => void;
  resumeIfNeeded: () => Promise<void>;
}

let instance: Promise<PlusInstance> | null = null;
/** The account `instance` was made for: the SDK keeps its customer for good, so another account gets its own. */
let instanceFor: string | null = null;
const currentAccount = () => {
  const id = useUserInfoStore.getState?.()?.info?.id;
  return id ? String(id) : null;
};

/**
 * plus.js with `create()`, for the queue only. Plus's CDN still serves an older SDK (no `create()`) for the
 * plain address and for the one the site loads (`?v=sheet-54-iframe-host`). Then a current copy is loaded
 * under a daily version, and the site's own `window.plus` is put back at once — so the site's Plus host
 * (iframes, Nelson…) stays exactly as it was.
 */
const loadCurrentSdk = () =>
  new Promise<any>((resolve, reject) => {
    const previous = (window as any).plus;
    const script = document.createElement('script');
    script.src = `${PLUS_BASE_URL}/sdk/plus.js?v=dabbeh-${new Date().toISOString().slice(0, 10)}`;
    script.async = true;
    script.dataset.dabbehPlus = 'true';
    script.onload = () => {
      const current = (window as any).plus;
      if (previous) (window as any).plus = previous;
      if (typeof current?.create === 'function') resolve(current);
      else reject(new Error('plus.js without create()'));
    };
    script.onerror = () => reject(new Error('plus.js did not load'));
    document.head.appendChild(script);
  });

/** This product's own Plus instance (other teams' Plus products on the page stay apart). */
export const loadPlus = (): Promise<PlusInstance> => {
  if (!isRealBilling()) return Promise.reject(new Error('plus off'));
  // signed out and in as someone else on this page (no reload): never the previous account's customer
  const account = currentAccount();
  if (instance && instanceFor === account) return instance;
  instanceFor = account;
  instance = ensurePlusSdkLoaded()
    .catch(() => null)
    .then(root => (typeof (root as any)?.create === 'function' ? root : loadCurrentSdk()))
    .then(root => (root as any).create({ productName: DABBEH_PLUS_PRODUCT, baseUrl: PLUS_BASE_URL }) as PlusInstance)
    .catch(error => {
      instance = null;
      throw error;
    });
  return instance;
};

/**
 * Plus's status → our subscription (null when not active). Without `current_period_end` the subscription is
 * active with its dates unknown: no end date is made up (the screens leave those rows out).
 */
export const toDabbehSubscription = (status: PlusStatus | null | undefined, pricePaid = DABBEH_PRICE_TOMAN): DabbehSubscription | null => {
  if (!status?.active) return null;
  const trackingCode = String(status.entitlements?.[0]?.id ?? status.customerId ?? '');
  const raw = typeof status.current_period_end === 'number' && status.current_period_end > 0 ? status.current_period_end : null;
  if (!raw) return { trackingCode, pricePaid };
  // unix seconds as documented; a value already in milliseconds is taken as it is (never «۲۰ هزار روز مانده»)
  const end = raw > 1e12 ? raw : raw * 1000;
  if (end <= Date.now()) return null;
  return {
    startedAt: new Date(end - DABBEH_SUBSCRIPTION_DAYS * DAY).toISOString(),
    endsAt: new Date(end).toISOString(),
    trackingCode,
    pricePaid,
  };
};

/**
 * The one-month plan — the only plan the screens sell («اشتراک یک‌ماهه»). Another plan is never charged in
 * its place; a product with a single plan is taken as that one.
 */
export const fetchPlan = async (): Promise<PlusPlan | null> => {
  if (!isRealBilling()) return null;
  try {
    const plans = await (await loadPlus()).getPlans();
    return plans.find(plan => plan.months === 1) ?? (plans.length === 1 ? plans[0] : null);
  } catch {
    return null;
  }
};

/** The plan's price in toman, or our constant when Plus is off or unreachable. */
export const fetchPlanPrice = async (): Promise<number> => {
  const plan = await fetchPlan();
  return plan && Number.isFinite(plan.amount) && plan.amount > 0 ? plan.amount : DABBEH_PRICE_TOMAN;
};

/** The patient's active subscription on Plus, or null (none, not logged in, or Plus off / unreachable). */
export const fetchSubscription = async (): Promise<DabbehSubscription | null> => {
  if (!isRealBilling()) return null;
  try {
    return toDabbehSubscription(await (await loadPlus()).getStatus(), await fetchPlanPrice());
  } catch {
    return null;
  }
};

/**
 * The patient's subscription as Plus sees it now: the subscription, `null` when Plus says there is none,
 * `undefined` when Plus could not be asked (off, not loaded, unreachable) — then nothing is decided.
 */
export const checkSubscription = async (): Promise<DabbehSubscription | null | undefined> => {
  if (!isRealBilling()) return undefined;
  try {
    return toDabbehSubscription(await (await loadPlus()).getStatus(), await fetchPlanPrice());
  } catch {
    return undefined;
  }
};

/** Back from the Katibe gateway: Plus put its resume marker in the address. */
export const isReturningFromGateway = (search: string) => /[?&](plus_resume_id|plus_status)=/.test(search);

/**
 * The address this page was opened with, read when this code first runs — before the site's own Plus host
 * (src/lib/plus/plusClient.ts, default instance) runs its resume on boot: finding no payment of its own, it
 * strips `plus_resume_id` from the address, and the queue's instance would then never finish the payment.
 * Taken once (a later visit of the page in the same tab is not a return from the gateway).
 */
const landing = typeof window !== 'undefined' ? { path: window.location.pathname, search: window.location.search } : null;
let landingTaken = false;
export const takeLandingSearch = (path: string) => {
  if (landingTaken || !landing || landing.path !== path) return '';
  landingTaken = true;
  return landing.search;
};

/**
 * Finishes this product's pending payment after the gateway: puts the resume marker back in the address and
 * lets the queue's instance resume at once (it reads and strips the marker in the same step).
 */
export const resumeAfterGateway = async (plus: PlusInstance, search: string) => {
  const resumeId = new URLSearchParams(search).get('plus_resume_id');
  if (!resumeId) return;
  const url = new URL(window.location.href);
  if (url.searchParams.get('plus_resume_id') !== resumeId) {
    url.searchParams.set('plus_resume_id', resumeId);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }
  await plus.resumeIfNeeded().catch(() => undefined);
};
export const gatewayFailed = (search: string) =>
  /cancel|fail|error|unpaid|reject|unsuccess/i.test(new URLSearchParams(search).get('plus_status') ?? '');
